/**
 * @file GrowthReport.tsx
 * @brief Super Admin report — user & company signup growth (composes analytics).
 *
 * @author DES
 * @date 27-07-2026
 */

import { useMemo, useState } from "react";
import { Building2, Users } from "lucide-react";
import { StatCard } from "@/features/analytics/components/StatCard";
import type { AnalyticsRange as RangeBarValue } from "@/features/analytics/components/AnalyticsRangeBar";
import {
  useCompanyGrowth,
  useUserGrowth,
} from "@/features/analytics/api";
import {
  ReportKpiCell,
  ReportKpiRow,
  ReportSectionHeader,
  ReportTableCell,
} from "@/features/reports-shared/ReportSection";
import { ReportShell } from "./ReportShell";
import { ReportTable } from "./ReportTable";

function defaultRange(): RangeBarValue {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
    groupBy: "day",
  };
}

export function GrowthReport() {
  const [range, setRange] = useState<RangeBarValue>(() => defaultRange());
  const trendRange = useMemo(
    () => ({
      startDate: range.startDate,
      endDate: range.endDate,
      groupBy: range.groupBy,
    }),
    [range],
  );

  const users = useUserGrowth(trendRange);
  const companies = useCompanyGrowth(trendRange);
  const userRows = users.data?.trends ?? [];
  const companyRows = companies.data?.trends ?? [];

  const merged = useMemo(() => {
    const map = new Map<
      string,
      { period: string; users: number; companies: number }
    >();
    for (const r of userRows) {
      map.set(r.period, {
        period: r.period,
        users: Number(r.count || 0),
        companies: 0,
      });
    }
    for (const r of companyRows) {
      const prev = map.get(r.period) ?? {
        period: r.period,
        users: 0,
        companies: 0,
      };
      prev.companies = Number(r.count || 0);
      map.set(r.period, prev);
    }
    return [...map.values()].sort((a, b) => a.period.localeCompare(b.period));
  }, [userRows, companyRows]);

  return (
    <ReportShell
      title="Growth"
      description="User and company signups for the selected range (group-by controls the period table)."
      range={range}
      onRangeChange={setRange}
      exportDisabled={merged.length === 0}
      serverExport={{
        scope: "admin",
        reportKey: "growth",
        params: {
          startDate: range.startDate,
          endDate: range.endDate,
          groupBy: range.groupBy,
        },
      }}
    >
      <>
        <ReportKpiRow className="xl:grid-cols-2">
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Users (total)"
              icon={Users}
              value={String(users.data?.totals?.total_users ?? 0)}
              hint={`${users.data?.totals?.total_active ?? 0} active`}
              isLoading={users.isLoading}
            />
          </ReportKpiCell>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Companies (total)"
              icon={Building2}
              value={String(companies.data?.totals?.total_companies ?? 0)}
              hint={`${companies.data?.totals?.total_active ?? 0} active`}
              isLoading={companies.isLoading}
            />
          </ReportKpiCell>
        </ReportKpiRow>

        <ReportSectionHeader
          title={`Signups per ${range.groupBy}`}
          description="Platform growth series from analytics growth endpoints."
        />
        <ReportTableCell>
          <ReportTable
            headers={[
              "Period",
              { label: "Users", align: "right" },
              { label: "Companies", align: "right" },
            ]}
            isLoading={users.isLoading || companies.isLoading}
            empty={merged.length === 0}
          >
            {merged.map((row) => (
              <tr key={row.period} className="hover:bg-secondary/20">
                <td className="px-4 py-2 font-medium">{row.period}</td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {row.users}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {row.companies}
                </td>
              </tr>
            ))}
          </ReportTable>
        </ReportTableCell>
      </>
    </ReportShell>
  );
}
